import { applyMemberStatusRoots, recompileMemberStatusDiff, recompileMemberStatuses, type MemberStatusStep, type MemberRecompileResult, type MemberRecompileScene } from '../compile/member-status-recompile.js';
import type { AbilityUseVerdict } from './ability-use.js';
import { statusRows, statusItemId, statusCasterId } from '../statuses/expiry.js';
import { readStatusRowStack } from '../compile/status-stack-reader.js';
import { encounterCreatureField } from '../../shared/array-types.js';
import { applyStatusAcDelta } from '../compile/creature-ac-slots.js';
import { createComputeSourceCleaner } from '../compile/compute-source-cleanup.js';
import { computeSourceBreakdown } from '../compile/computed-persistence.js';
import { C1_CONTRACT } from '../compile/possessor-compile.js';
import { REDUCE_DAMAGE_ROLL } from './weapon-attack-extra-damage.js';
import { statusRowOnlyLayer } from '../statuses/runtime-layers.js';
import { itemField, record, scalar } from '../ability/context.js';
import { explicitWeaponStatusIsRuntimeOnly } from './explicit-weapon-attack.js';
import { isAttackPassiveWindow, changesStatusAttackBudget, projectStatusAttackBudget, statusProjectionDependencies, statusDefinitionLayerNames } from '../compile/status-attack-budget.js';
import { fortunateDefinitionSupported } from './weapon-attack-fortunate.js';
import { statusHasCompileIdDependency } from '../compile/member-status-diff.js';
import { surfaceStatusRuntimeOnly } from '../compile/surface-status-runtime.js';
import { projectAuraOwnerSkills } from '../compile/aura-owner-skill-projection';
import { createStatusLayerReadContext } from '../compile/status-row-construction';
import { compileStatusResidueFields, applyStatusRootFields } from '../compile/status-root-residue';
import { STATUS_RESIDUAL_LAYERS } from '../compile/status-residue-layers';
import { layerReadView } from '../compile/layer-read-view';

type Row = Record<string, unknown>;
export interface AttackStatusState { ids: string[]; target: Row[]; caster: Row[] }
export function mergeAttackStatuses(row: Row, state: AttackStatusState): { target: Row[]; caster: Row[] } {
  const ids = new Set(state.ids.map(id => id.toUpperCase()));
  const kept = (side: 'target' | 'caster'): Row[] => statusRows(row, side).filter(s => !ids.has(String(s.status_id ?? '').toUpperCase()));
  return { target: [...state.target, ...kept('target')], caster: [...state.caster, ...kept('caster')] };
}
/** The page scene of a member's light / condition gates (websocket/possessor-scene.ts); absent → such a gate refuses by name. */
export type AttackStatusScene = (row: Row, members?: readonly Row[]) => MemberRecompileScene | undefined;
/** One synchronous attack decision, bound to its immutable original rows, catalog, roster and scene. */
export type AttackStatusProofs = WeakMap<Row, Map<string, string | null>>;
/** Live roll modifiers and layerless markers change no derived cluster. */
export function attackStatusHasOnlyRuntimeLayers(definition: Row | null): boolean {
  if (definition && statusHasCompileIdDependency(definition)) return false;
  if (surfaceStatusRuntimeOnly(definition)) return true;
  // The pinned Fortunate payload reads its next-roll modifier live; its debit already belongs to the action frame.
  if (definition && fortunateDefinitionSupported(record(definition.item_layer_types) ?? {})) return true;
  if (definition && explicitWeaponStatusIsRuntimeOnly(definition)) return true;
  const runtimeLayers = new Set(['roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis',
    'no_actions', 'no_reactions', 'ap_change_at_start_of_turn']);
  const keys = Object.keys(record(definition?.item_layer_types) ?? {});
  if (isAttackPassiveWindow(definition) && !keys.includes('extra_attack')) return true;
  // An attack spell is itself the lingering rider. Its cast metadata and lifetime do not change a derived cluster; ending Guiding Bolt
  // must not recompile unrelated equipment/ability buckets. A stat/skill/ability grant still takes the full projection path.
  if (keys.includes('triggers_on_next_roll_type_against')) for (const key of ['triggers_on_next_roll_type_against',
    'effect_ends_on_end_of_caster_next_turn', 'effect_ends_on_start_of_caster_next_turn', 'range', 'dice', 'die', 'damage_type', 'roll_type',
    'spell_level', 'spell_casting_time', 'spell_component_verbal', 'spell_component_somatic', 'spell_circle', 'spell_sphere',
    'spell_upcastable', 'die_upcast', 'dice_upcast']) runtimeLayers.add(key);
  return !!definition && keys.every(k => runtimeLayers.has(k) || statusRowOnlyLayer(k));
}
function compileStatuses(row: Row, steps: MemberStatusStep[], definition: (id: string) => Row | null,
  roster: ReadonlyMap<string, Row>, scene?: AttackStatusScene): MemberRecompileResult | null {
  if (!steps.length) return null;
  const diff = recompileMemberStatusDiff(row, steps, { definition, catalog: record(row.catalog) });
  if (diff) return diff;
  const windowSteps = steps.filter(step => changesStatusAttackBudget(definition(step.statusItemId)));
  if (windowSteps.length) {
    const other = compileStatuses(row, steps.filter(step => !windowSteps.includes(step)), definition, roster, scene);
    if (other && !other.ok) return other;
    const result = projectStatusAttackBudget(other ? { ...row, computed: { ...record(row.computed), creature: other.creature } } : row, steps, definition);
    if (!result.ok) return result;
    const creature = { ...(other?.creature ?? record(record(row.computed)?.creature) ?? {}) };
    Object.assign(creature, result.budget);
    return { ...other, ok: true, creature, vitality: other?.vitality ?? record(record(row.computed)?.vitality) ?? {},
      rootScalars: other?.rootScalars ?? {}, catalog: other?.catalog ?? record(row.catalog), addIds: other?.addIds ?? [] };
  }
  // These layers are read from live status rows by rolls/action gates/turn resets.
  // Adding or retiring them changes no derived cluster; immediate locks are in the verdict's EC writes.
  steps = steps.filter(s => !attackStatusHasOnlyRuntimeLayers(definition(s.statusItemId)));
  if (!steps.length)
    return { ok: true, creature: record(record(row.computed)?.creature) ?? {},
      vitality: record(record(row.computed)?.vitality) ?? {}, rootScalars: {}, catalog: record(row.catalog), addIds: [] };
  const definitions = steps.map(s => definition(s.statusItemId));
  // A pure mitigation/reduction buff changes no ability bucket, skill, equipment or essence. Use the
  // compiler's existing AC delta directly; a full equipment compile would re-judge unrelated grants.
  const allowed = new Set(['damage_mitigation', 'roll_type', 'dice', 'die', 'mod', 'range', 'cost_gp', 'ability_charges',
    'recharge_range_begin', 'recharge_range_end', 'override_ability', 'if_armor_type_equipped_active',
    'triggers_when_caster_takes_any_damage', 'effect_ends_on_start_of_caster_next_turn',
    'triggers_on_roll_type_against_the_caster', 'triggers_on_hit', 'unenchantable']);
  const simple = definitions.every(d => d && record(d.item_layer_types) && Object.keys(record(d.item_layer_types)!).every(k =>
    allowed.has(k) || /^requires_skill_tier_\d+$/.test(k)) &&
    (record(d.item_layer_types)!.damage_mitigation !== undefined || scalar(record(d.item_layer_types)!.roll_type) === REDUCE_DAMAGE_ROLL));
  if (simple) {
    const prior = record(record(row.computed)?.creature) ?? {};
    let creature = prior;
    const typeOf = (id: string): string => String(itemField(definition(id), 'item_type') ?? '');
    const cleaner = createComputeSourceCleaner({ catalogRows: C1_CONTRACT.catalogRows, abilityTypes: C1_CONTRACT.abilityTypes,
      aggregationStrategy: name => C1_CONTRACT.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
      resolveCarrierRef: definition, resolveInventoryInstance: () => null,
      resolveItemName: id => itemField(definition(id), 'item_name') ?? '', queryItemType: typeOf });
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]!, def = definitions[i]!, layers = record(def.item_layer_types)!;
      if (layers.damage_mitigation === undefined) continue;
      const delta = applyStatusAcDelta({ priorAc: record(creature.ac) ?? {}, priorDamageMitigation: creature.damage_mitigation,
        layers: { damage_mitigation: layers.damage_mitigation }, statusItemId: step.statusItemId,
        statusName: String(itemField(def, 'item_name') ?? ''), applied: step.kind === 'add',
        stacks: step.kind === 'add' ? step.stack : readStatusRowStack(statusRows(row, 'target').find(s => String(s.status_id).toUpperCase() === step.statusId.toUpperCase()) ?? {}),
        clean: rows => Object.values(cleaner.clean(rows, row)) as Row[], typeOf,
        breakdown: rows => computeSourceBreakdown(rows) as Row });
      if (!delta.ok) return delta;
      // The AC delta owns its cluster and source rows; borrow every untouched root.
      if (creature === prior) creature = { ...prior };
      creature.ac = delta.ac;
      if (delta.damageMitigation === undefined) delete creature.damage_mitigation; else creature.damage_mitigation = delta.damageMitigation;
    }
    return { ok: true, creature, vitality: record(record(row.computed)?.vitality) ?? {}, rootScalars: {}, catalog: record(row.catalog), addIds: [] };
  }
  try {
    const ownId = String(encounterCreatureField(row.encounter_creatures, 'item_id') ?? '').toUpperCase();
    return recompileMemberStatuses(row, steps, { definition: id => id.toUpperCase() === ownId ? row : definition(id), authoredDefinition: definition,
      catalog: row.catalog as Row | null, roster: () => [...roster.values()], ...scene?.(row, [...roster.values()]) },
      // Native status families already compile on NPCs one at a time. Reuse the
      // derived-only chain for their combined delta; ability/grant chains retain their existing boundary.
      { derivedOnly: definitions.every(d => itemField(d, 'item_type') === 'status_effect') });
  } catch (error) { return { ok: false, reason: 'compile-threw:' + String(error instanceof Error ? error.message : error) }; }
}
/** The same projection contract is proved on the planned changes before the swing draws. */
export function attackStatusPlanFailure(row: Row, verdict: Extract<AbilityUseVerdict, { kind: 'resolved' }>,
  definition: (id: string) => Row | null, roster: ReadonlyMap<string, Row>, scene?: AttackStatusScene, proofs?: AttackStatusProofs): string | null {
  const target = String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
  const before = statusRows(row, 'target'), steps: MemberStatusStep[] = [];
  for (const s of verdict.statusesRemoved.filter(s => s.target.toUpperCase() === target)) {
    for (const stored of before.filter(o => s.status_id ? String(o.status_id ?? '').toUpperCase() === s.status_id.toUpperCase() : statusItemId(o) === s.status_item_id.toUpperCase()))
      steps.push({ kind: 'remove', statusItemId: statusItemId(stored), statusId: String(stored.status_id ?? '').toUpperCase() });
  }
  for (const s of verdict.statusStacks.filter(s => s.target.toUpperCase() === target)) {
    const stored = before.find(o => String(o.status_id ?? '').toUpperCase() === s.status_id.toUpperCase());
    if (!stored) return 'attack:status-projection:status-not-held';
    steps.push({ kind: 'remove', statusItemId: statusItemId(stored), statusId: s.status_id },
      { kind: 'add', statusItemId: statusItemId(stored), casterId: statusCasterId(stored), stack: s.status_stack });
  }
  steps.push(...verdict.statusesAdded.filter(s => s.target.toUpperCase() === target).map(s =>
    ({ kind: 'add' as const, statusItemId: s.status_item_id, casterId: s.caster, stack: s.status_stack })));
  const key = JSON.stringify(steps), held = proofs?.get(row);
  if (held?.has(key)) return held.get(key)!;
  const result = compileStatuses(row, steps, definition, roster, scene);
  // Removing a form still saves the decided rows when its local source window
  // is incomplete; the saved-frame installer owns the existing recovery path.
  if (result && !result.ok && steps.every(step => step.kind === 'remove')
    && steps.some(step => itemField(definition(step.statusItemId), 'item_type') === 'creature')) return null;
  const failure = result && !result.ok ? 'attack:status-projection:' + result.reason : null;
  if (proofs) { const cache = held ?? new Map<string, string | null>(); cache.set(key, failure); proofs.set(row, cache); }
  return failure;
}
/** Install decided status rows even when their derived projection fails.
 * Callers carry the named failure separately from those authoritative rows. */
export function projectAttackStatuses(row: Row, state: AttackStatusState,
  definition: (id: string) => Row | null, roster: ReadonlyMap<string, Row>, scene?: AttackStatusScene): string | null {
  const before = statusRows(row, 'target'), after = mergeAttackStatuses(row, state).target;
  const id = (s: Row): string => String(s.status_id ?? '').toUpperCase();
  const changed = (s: Row, other: Row[]): boolean => !other.some(o => id(o) === id(s) && statusItemId(o) === statusItemId(s)
    && readStatusRowStack(o) === readStatusRowStack(s));
  const steps: MemberStatusStep[] = before.filter(s => changed(s, after)).map(s => ({ kind: 'remove', statusItemId: statusItemId(s), statusId: id(s) }));
  steps.push(...after.filter(s => changed(s, before)).map(s => ({ kind: 'add' as const, statusItemId: statusItemId(s),
    casterId: statusCasterId(s), stack: readStatusRowStack(s), statusId: id(s) })));
  let result: MemberRecompileResult | null;
  try {
    if (steps.length && steps.every(step => {
      const def = definition(step.statusItemId);
      return def && statusDefinitionLayerNames(def).includes('if_aura_owner_has_status_effect_active');
    })) {
      const readers = createStatusLayerReadContext(statusProjectionDependencies(row, definition));
      const holder = { ...row, statuses: mergeAttackStatuses(row, state) };
      let skills = record(record(row.computed)?.creature)?.skills;
      const owners = new Set([...before, ...after].filter(status => state.ids.includes(id(status))).map(statusCasterId));
      for (const ownerId of owners) {
        const projected = projectAuraOwnerSkills({ holder, previousHolder: row, ownerId, skills, definition,
          resolveOwner: owner => roster.get(owner.toUpperCase()) ?? null,
          layerValue: readers.layerValue, layerEntries: readers.layerEntries });
        if (!projected.ok) { row.statuses = holder.statuses; return projected.reason; }
        skills = projected.skills;
      }
      result = { ok: true, creature: { ...record(record(row.computed)?.creature), skills },
        vitality: record(record(row.computed)?.vitality) ?? {}, rootScalars: {}, catalog: record(row.catalog), addIds: [] };
    } else result = compileStatuses(row, steps, definition, roster, scene);
  }
  catch (error) { result = { ok: false, reason: 'compile-threw:' + String(error instanceof Error ? error.message : error) }; }
  const nextStatuses = mergeAttackStatuses(row, state);
  // A saved removal remains authoritative when an unrelated derived cluster
  // has an existing named gap. Retract its flattened shares even in that case.
  if (result && !result.ok) {
    const readers = createStatusLayerReadContext(statusProjectionDependencies(row, definition));
    const layers = [...new Set(steps.flatMap(step => {
      const def = definition(step.statusItemId);
      return def ? statusDefinitionLayerNames(def).filter(key => STATUS_RESIDUAL_LAYERS.includes(key)) : [];
    }))];
    const after = layerReadView(row, { statuses: nextStatuses });
    applyStatusRootFields(row, compileStatusResidueFields(row, after, {
      definition, entries: (source, key) => Object.values(readers.layerEntries(source, key)), value: readers.layerValue,
      quantityIds: C1_CONTRACT.rollup.quantityIds }, layers));
  }
  row.statuses = nextStatuses;
  if (result) {
    if (!result.ok) return 'attack:status-projection:' + result.reason;
    const computed = (row.computed ??= {}) as Row;
    computed.creature = result.creature; computed.vitality = result.vitality;
    applyMemberStatusRoots(row, result);
    if (result.catalog) row.catalog = result.catalog;
  }
  return null;
}
